import { useCallback, useEffect, useMemo, useState } from 'react'
import SystemHeader from '@/widgets/layout/SystemHeader'
import { useAppNavigate } from '../../hooks/useAppNavigate'
import { useBackToParent, usePersistentPageSize } from '@/hooks'
import { queriesApi } from '@/services'
import { isHandledAuthError } from '@/services/client'
import { useToast } from '@/widgets/common'
import type { BankTransferQueryRowResponse } from '@/services/queries/types'
import { toCsvString, downloadCsvString } from '@/utils/download'
import useSearchPersistence from '@/hooks/useSearchPersistence'
import { Pagination, BackButton, TableLoadingRow, DemoInput, DemoSelect, DownloadIcon } from '@/widgets/demo/ListPrimitives';
import { demoButtonClass, computePagination } from '@/widgets/demo/listHelpers';
const TARGET_SCOPE_OPTIONS = [
  { value: 'LATEST', label: '振込対象（直近振込）' },
  { value: 'ALL', label: '全ての振込' },
]

// 最後に適用した検索条件とページを localStorage に保持する（顧客要望：
// 「検索条件は最後に選択した結果を保持する」）。バリデーション実行状態などの一時状態は保持しない。
interface BankTransferPersisted {
  identId: string
  transferDate: string
  targetScope: string
  page: number
}

const DEFAULT_PERSISTED: BankTransferPersisted = {
  identId: '',
  transferDate: '',
  targetScope: 'LATEST',
  page: 1,
}

const BankTransferExport = () => {
  const navigate = useAppNavigate()
  const goBackToParent = useBackToParent()
  const toast = useToast()
  const [generated, setGenerated] = useState(false)

  const [savedSearch, setSavedSearch] = useSearchPersistence<BankTransferPersisted>('bank-transfer-export', DEFAULT_PERSISTED)

  const [qIdentId, setQIdentId] = useState(savedSearch.identId)
  const [qTransferDate, setQTransferDate] = useState(savedSearch.transferDate)
  const [qTargetScope, setQTargetScope] = useState(savedSearch.targetScope)
  const [applied, setApplied] = useState({
    identId: savedSearch.identId,
    transferDate: savedSearch.transferDate,
    targetScope: savedSearch.targetScope,
  })

  const [rows, setRows] = useState<BankTransferQueryRowResponse[]>([])
  const [totalElements, setTotalElements] = useState(0)
  const [totalPages, setTotalPages] = useState(1)
  const [page, setPage] = useState(savedSearch.page ?? 1)
  const [pageSize, setPageSize] = usePersistentPageSize('bank-transfer-export')
  const [loading, setLoading] = useState(false)

  const fetchData = useCallback(async () => {
    setLoading(true)
    try {
      const { data } = await queriesApi.bankTransfer({
        identId: applied.identId.trim() || undefined,
        transferDate: applied.transferDate || undefined,
        targetScope: applied.targetScope,
        page: page - 1,
        size: pageSize,
      })
      // 取得ページが範囲外（永続化された page が絞り込み後の総ページ数を超える等）で
      // 0件返却された場合、該当データはあるのに「データなし」表示になる。最終ページへ寄せて再取得する。
      if (data.content.length === 0 && data.totalElements > 0 && page > 1) {
        setTotalElements(data.totalElements)
        setTotalPages(Math.max(1, data.totalPages || 1))
        setPage(Math.max(1, Math.ceil(data.totalElements / pageSize)))
        return
      }
      setRows(data.content)
      setTotalElements(data.totalElements)
      setTotalPages(Math.max(1, data.totalPages || 1))
    } catch (error) {
      setRows([])
      setTotalElements(0)
      setTotalPages(1)
      if (isHandledAuthError(error)) return
    } finally {
      setLoading(false)
    }
  }, [applied, page, pageSize])

  useEffect(() => {
    // 検索条件・ページ変更に応じて一覧を取得する正当なデータ取得 effect。
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void fetchData()
  }, [fetchData])

  const runSearch = () => {
    setLoading(true)
    setPage(1)
    const nextApplied = {
      identId: qIdentId,
      transferDate: qTransferDate,
      targetScope: qTargetScope,
    }
    setApplied(nextApplied)
    setSavedSearch({ ...nextApplied, page: 1 })
  }

  const runClear = () => {
    setLoading(true)
    setQIdentId('')
    setQTransferDate('')
    setQTargetScope('LATEST')
    setPage(1)
    const clearedApplied = {
      identId: '',
      transferDate: '',
      targetScope: 'LATEST',
    }
    setApplied(clearedApplied)
    setSavedSearch({ ...clearedApplied, page: 1 })
  }

  const totalAmount = useMemo(
    () => rows.reduce((sum, row) => sum + (row.amount ?? 0), 0),
    [rows],
  )

  const { safePage, rangeFrom, rangeTo } = computePagination(page, totalElements, pageSize)

  const latestTransferDate = useMemo(() => {
    const values = rows.map((row) => row.transferDate).filter(Boolean)
    if (values.length === 0) return '－'
    return values[0] as string
  }, [rows])

  const handleValidate = () => {
    setGenerated(true)
    toast.success('バリデーションが完了しました')
  }

  const downloadAll = async () => {
    try {
      const { data } = await queriesApi.bankTransfer({
        identId: applied.identId.trim() || undefined,
        transferDate: applied.transferDate || undefined,
        targetScope: applied.targetScope,
        page: 0,
        size: Math.max(1000, totalElements || 1000),
      })
      const headers = ['リスト名', '識別ID', '受取人名', '銀行コード', '支店コード', '預金種目', '口座番号', '振込指定日']
      const values = data.content.map((row) => [
        row.jobName,
        row.identId,
        row.name,
        row.bankCode,
        row.bankBranchCode,
        row.bankAccountTypeLabel,
        row.bankAccountNumber,
        row.transferDate ?? '',
      ])
      const stamp = new Date().toISOString().slice(0, 19).replace(/[-:T]/g, '')
      downloadCsvString(toCsvString(headers, values), `全銀データ_${stamp}.csv`)
    } catch (error) {
      if (isHandledAuthError(error)) return
    }
  }

  return (
    <div className="min-h-screen bg-gray-100">
      <SystemHeader breadcrumbs={[{ label: '振込データ出力' }]} />
      <div className="mx-auto max-w-6xl px-6 py-8">
        <BackButton onClick={goBackToParent} />

        <div className="mb-6 grid grid-cols-1 gap-6 md:grid-cols-2">
          <div className="rounded-xl bg-white p-6 shadow-lg">
            <h2 className="mb-4 text-xl font-bold text-gray-800">振込データ検索</h2>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">識別ID</label>
                <DemoInput type="text" value={qIdentId} onChange={(e) => setQIdentId(e.target.value)} placeholder="例: SID-U-001234" className="w-full" />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">振込指定日</label>
                <DemoInput type="date" value={qTransferDate} onChange={(e) => setQTransferDate(e.target.value)} className="w-full" />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">対象フィルタ</label>
                <DemoSelect value={qTargetScope} onChange={(e) => setQTargetScope(e.target.value)} className="w-full">
                  {TARGET_SCOPE_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </DemoSelect>
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">件数</label>
                <DemoInput type="text" value={`${totalElements}`} readOnly className="w-full bg-gray-50" />
              </div>
              <div className="col-span-2">
                <label className="mb-1 block text-sm font-medium text-gray-700">最新振込日</label>
                <DemoInput type="text" value={latestTransferDate} readOnly className="w-full bg-gray-50" />
              </div>
            </div>
            <div className="mt-4 flex justify-end gap-3">
              <button type="button" onClick={runClear} className={demoButtonClass.secondary}>クリア</button>
              <button type="button" onClick={runSearch} className={demoButtonClass.primary}>検索</button>
            </div>
          </div>

          <div className="rounded-xl bg-white p-6 shadow-lg">
            <h2 className="mb-4 text-xl font-bold text-gray-800">出力サマリー</h2>
            <div className="grid grid-cols-2 gap-4">
              <div className="rounded-lg bg-indigo-50 p-4 text-center">
                <p className="mb-1 text-xs text-indigo-600">振込対象件数</p>
                <p className="text-3xl font-bold text-indigo-800">{totalElements}</p>
                <p className="text-xs text-indigo-500">件</p>
              </div>
              <div className="rounded-lg bg-indigo-50 p-4 text-center">
                <p className="mb-1 text-xs text-indigo-600">振込対象金額</p>
                <p className="text-3xl font-bold text-indigo-800">¥{totalAmount.toLocaleString()}</p>
                <p className="text-xs text-indigo-500">円</p>
              </div>
            </div>
            <div className="mt-4 rounded-lg border border-gray-200 p-4">
              <p className="mb-2 text-sm font-medium text-gray-800">フォーマット情報</p>
              <div className="space-y-1 text-xs text-gray-600">
                <p>種別: 全銀協フォーマット（デモ）</p>
                <p>文字コード: JIS / Shift-JIS</p>
                <p>レコード長: 120バイト固定長</p>
              </div>
            </div>
            {generated && (
              <div className="mt-4 rounded-lg border border-green-200 bg-green-50 p-4">
                <div className="flex items-start gap-2">
                  <svg className="h-5 w-5 shrink-0 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <div>
                    <p className="text-sm font-medium text-green-800">バリデーション完了 - エラーなし</p>
                    <p className="text-xs text-green-700">全{totalElements}件のデータが正常にフォーマット変換されました。</p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="rounded-xl bg-white p-6 shadow-lg">
          <h2 className="mb-4 text-lg font-bold text-gray-800">振込対象一覧（プレビュー）</h2>
          <div className="overflow-x-auto rounded-lg border border-gray-200">
            <table className="w-full">
              <thead className="border-b border-gray-200 bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">No.</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">リスト名</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">識別ID</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">受取人名</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">銀行コード</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">支店コード</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">預金種目</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">口座番号</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">振込指定日</th>
                </tr>
              </thead>
              <tbody className={`divide-y divide-gray-200 text-sm transition-opacity ${loading ? 'opacity-60' : ''}`}>
                {rows.length === 0 ? (
                  loading ? (
                    <TableLoadingRow colSpan={9} />
                  ) : (
                    <tr>
                      <td colSpan={9} className="px-4 py-8 text-center text-sm text-gray-500">該当するデータがありません</td>
                    </tr>
                  )
                ) : (
                  rows.map((row, index) => (
                    <tr key={`${row.jobId}-${row.identId}-${index}`} className="transition hover:bg-blue-50">
                      <td className="px-4 py-3">{index + 1}</td>
                      <td className="px-4 py-3">{row.jobName}</td>
                      <td className="px-4 py-3 font-mono text-xs">{row.identId}</td>
                      <td className="px-4 py-3">{row.name}</td>
                      <td className="px-4 py-3 font-mono">{row.bankCode}</td>
                      <td className="px-4 py-3 font-mono">{row.bankBranchCode}</td>
                      <td className="px-4 py-3">{row.bankAccountTypeLabel}</td>
                      <td className="px-4 py-3 font-mono">{row.bankAccountNumber}</td>
                      <td className="px-4 py-3">{row.transferDate ?? '－'}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          <Pagination
            currentPage={safePage}
            totalPages={totalPages}
            total={totalElements}
            rangeFrom={rangeFrom}
            rangeTo={rangeTo}
            onPageChange={(next) => { setLoading(true); setPage(next); setSavedSearch({ ...applied, page: next }); }}
            pageSize={pageSize}
            onPageSizeChange={(next) => { setLoading(true); setPageSize(next); setPage(1); setSavedSearch({ ...applied, page: 1 }); }}
          />
          <div className="mt-6 flex justify-end gap-3">
            <button type="button" onClick={() => navigate('home')} className={demoButtonClass.secondary}>戻る</button>
            <button type="button" onClick={handleValidate} className={demoButtonClass.primary}>バリデーション実行</button>
            <button
              type="button"
              onClick={downloadAll}
              disabled={!generated || rows.length === 0}
              className={!generated || rows.length === 0 ? demoButtonClass.disabled : `flex items-center space-x-2 ${demoButtonClass.success}`}
            >
              <DownloadIcon />
              <span>全銀データ出力</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default BankTransferExport
